import cn from 'classnames';
import { useCallback, useMemo } from 'react';
import { Button } from '@Components/Button';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { BPMTable, Header } from '@Components/StickyTable';
import { TableLoading } from '@Components/TableEmptyState';
import { TablePage } from '@Components/TablePage';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { AmcInstance } from '../amc-instances-types';
import { modeLabel, modeNote, writeSuccess } from '../amc-instances-utils';
import { useDeleteInstance } from '../utils/useAmcInstances';

interface InstancesBodyProps {
  instances?: AmcInstance[];
  isLoading: boolean;
  error: unknown;
  live: boolean;
  onEdit: (instance: AmcInstance) => void;
}

export const InstancesBody = ({
  instances,
  isLoading,
  error,
  live,
  onEdit
}: InstancesBodyProps) => {
  // Kept stable so BPMTable doesn't reset its sort/filter state every render.
  const headers = useMemo<Header[]>(
    () => [
      { label: 'Instance Name', name: 'instanceName', flex: 1.5 },
      { label: 'Instance ID', name: 'instanceId', flex: 1 },
      { label: 'Advertiser', name: 'advertiserName', flex: 1.5 },
      {
        label: 'Status',
        name: 'status',
        flex: 1,
        renderer: (instance: AmcInstance) => (
          <span className={cn('amc-status', instance.status?.toLowerCase())}>
            {instance.status ?? '—'}
          </span>
        )
      },
      { label: 'Type', name: 'instanceType', flex: 1 },
      { label: 'S3 Bucket', name: 's3BucketName', flex: 1.5 },
      { label: 'AWS Account', name: 'awsAccountId', flex: 1 },
      {
        label: '',
        name: 'actions',
        width: 160,
        nonInteractive: true,
        nonFilterable: true,
        renderer: (instance: AmcInstance) => (
          <InstanceActions instance={instance} live={live} onEdit={onEdit} />
        )
      }
    ],
    [live, onEdit]
  );

  const renderTableLoading = useCallback(
    () => <TableLoading isLoading={isLoading} error={error} emptyMessage='No instances found' />,
    [isLoading, error]
  );

  return (
    <>
      {live && (
        <div className='amc-banner live' data-testid='amc-live-banner'>
          LIVE mode is on — creates, edits, deletes, and advertiser changes will be pushed to the
          real Tinuiti AMC account on Amazon.
        </div>
      )}
      <TablePage tableClassName='amc-instances-table' dataTestId='amc-instances-table'>
        <BPMTable<AmcInstance>
          headers={headers}
          data={instances}
          filterBar
          pagination
          limit={25}
          alternateColors
          emptyDisplay='—'
          noRowsRenderer={renderTableLoading}
        />
      </TablePage>
    </>
  );
};

interface InstanceActionsProps {
  instance: AmcInstance;
  live: boolean;
  onEdit: (instance: AmcInstance) => void;
}

const InstanceActions = ({ instance, live, onEdit }: InstanceActionsProps) => {
  const deleteInstance = useDeleteInstance();
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();

  const handleDelete = async () => {
    const name = instance.instanceName ?? instance.instanceId;
    try {
      await setAreYouSure({
        title: `Delete “${name}”?`,
        message: live ? `${modeNote(live)} This cannot be undone.` : modeNote(live),
        okayText: modeLabel('Delete', live),
        intent: live ? Intent.ERROR : Intent.WARNING
      });
    } catch {
      return; // cancelled
    }

    try {
      await deleteInstance.mutateAsync({ instanceId: instance.instanceId, dryRun: !live });
      if (live) {
        Mixpanel.track(MxE.RecordDeleted, {
          page: PAGE_NAMES.AMC_INSTANCES,
          entity: 'amc_instance'
        });
      }
      setError(writeSuccess(live, `Delete “${name}”`));
    } catch (e) {
      setError({ message: `Couldn't delete “${name}”: ${e.message}`, reportError: e });
    }
  };

  return (
    <div className='amc-row-actions' data-testid={`amc-instance-actions-${instance.instanceId}`}>
      <Button
        size={Size.SM}
        appearance={Appearance.OUTLINED}
        design={Design.NEUTRAL}
        data-testid='amc-edit-instance'
        onClick={() => onEdit(instance)}
      >
        Edit
      </Button>
      <Button
        size={Size.SM}
        appearance={Appearance.OUTLINED}
        design={Intent.ERROR}
        loading={deleteInstance.isPending}
        data-testid='amc-delete-instance'
        onClick={handleDelete}
      >
        Delete
      </Button>
    </div>
  );
};
